/*
 * Sélecteur de réparation visuel.
 *
 * Sur une page d'appareil, les modèles sont visibles immédiatement ; ailleurs,
 * deux gestes suffisent : toucher l'appareil, puis le modèle. Le tableau des
 * prix TTC s'affiche alors sous les tuiles. Les
 * illustrations et la liste des modèles viennent de `repair-models.json` ; les
 * réparations et leurs prix de `repair-services-index.json`. Le balisage des
 * tuiles de modèle reproduit exactement celui du snippet
 * `snippets/model-picker-tiles.liquid` rendu côté serveur.
 */
class RepairSelector extends HTMLElement {
  async connectedCallback() {
    this.deviceStep = this.querySelector('[data-repair-step="device"]');
    this.modelPanel = this.querySelector('[data-repair-model-panel]');
    this.modelsList = this.querySelector('[data-repair-models-list]');
    this.step2Title = this.querySelector('[data-repair-step2-title]');
    this.results = this.querySelector('[data-repair-results]');
    this.resultsTitle = this.querySelector('[data-repair-results-title]');
    this.status = this.querySelector('[data-repair-status]');
    this.rowsBody = this.querySelector('[data-repair-rows]');
    this.empty = this.querySelector('[data-repair-empty]');
    this.modelLink = this.querySelector('[data-repair-model-link]');
    this.collectionUrls = this.readCollectionUrls();

    this.panelId = this.modelPanel?.id || 'RepairModels';
    this.rows = [];
    this.models = null;
    this.currentDevice = '';
    this.currentModel = '';
    this.reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false;

    const locale = (this.dataset.locale || document.documentElement.lang || 'fr').toLowerCase();
    this.language = locale.split('-')[0];
    this.money = new Intl.NumberFormat(`${this.language}-BE`, { style: 'currency', currency: 'EUR' });
    this.collator = new Intl.Collator(this.language, { numeric: true, sensitivity: 'base' });

    this.deviceButtons = [...this.querySelectorAll('[data-device]')];
    if (this.deviceButtons.length === 0) return;

    // Les tuiles du bon appareil sont déjà dans le HTML des pages piliers.
    // Leur sélection ne dépend donc pas du téléchargement des deux index.
    if (this.dataset.preselect && this.modelsList?.children.length) {
      this.selectDevice(this.dataset.preselect, { focus: false, scroll: false, rebuild: false });
    }

    this.dataReady = Promise.all([this.loadRows(), this.loadModels()]);
    this.userInteracted = false;
    this.deviceButtons.forEach((button) => {
      button.addEventListener('click', async () => {
        this.userInteracted = true;
        await this.dataReady;
        this.selectDevice(button.dataset.device, { focus: true, scroll: true });
      });
    });

    if (this.modelsList) {
      this.modelsList.addEventListener('click', async (event) => {
        const tile = event.target.closest('[data-model]');
        if (tile && this.modelsList.contains(tile)) {
          this.userInteracted = true;
          await this.dataReady;
          if (this.modelsList.contains(tile)) this.selectModel(tile.dataset.model, { scroll: true });
        }
      });
    }

    await this.dataReady;
    if (this.userInteracted) return;

    const params = new URLSearchParams(window.location.search);
    const requested = this.matchDevice(params.get('appareil') || params.get('device') || this.dataset.preselect);
    if (requested) {
      const hasServerTiles = Boolean(this.modelsList && this.modelsList.children.length);
      this.selectDevice(requested, { focus: false, scroll: false, rebuild: !hasServerTiles });
      const model = this.matchModel(requested, params.get('modele') || params.get('model'));
      if (model) this.selectModel(model, { scroll: false });
    }
  }

  readCollectionUrls() {
    const node = this.querySelector('[data-repair-collections]');
    if (!node) return {};
    try {
      const parsed = JSON.parse(node.textContent);
      delete parsed._;
      return parsed;
    } catch (_error) {
      return {};
    }
  }

  async loadRows() {
    if (!this.dataset.source) return;
    try {
      const response = await fetch(this.dataset.source, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const remoteRows = await response.json();
      if (!Array.isArray(remoteRows)) return;
      const root = window.Shopify?.routes?.root || '/';
      const routeRoot = root.endsWith('/') ? root : `${root}/`;
      const suffix = this.language === 'nl' ? 'Nl' : this.language === 'en' ? 'En' : '';
      this.rows = remoteRows
        .map((source) => {
          const handle = source[`handle${suffix}`] || source.handle;
          return {
            device: source[`device${suffix}`] || source.device,
            model: source[`model${suffix}`] || source.model,
            issue: source[`issue${suffix}`] || source.issue,
            modelTag: source.modelTag || '',
            price: Number.isFinite(source.price) ? source.price : null,
            url: handle ? `${routeRoot}products/${handle}` : '',
          };
        })
        .filter((row) => row.device && row.model && row.issue && row.url);
    } catch (error) {
      console.warn('Repair selector price dataset could not be loaded.', error);
    }
  }

  async loadModels() {
    if (!this.dataset.models) return;
    try {
      const response = await fetch(this.dataset.models, { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const parsed = await response.json();
      if (parsed && Array.isArray(parsed.devices)) this.models = parsed;
    } catch (error) {
      console.warn('Repair selector model dataset could not be loaded.', error);
    }
  }

  selectDevice(deviceName, { focus = false, scroll = false, rebuild = true } = {}) {
    if (!deviceName) return;
    this.currentDevice = deviceName;
    this.currentModel = '';

    this.deviceButtons.forEach((button) => {
      const active = button.dataset.device === deviceName;
      button.setAttribute('aria-expanded', String(active));
      button.classList.toggle('is-selected', active);
      if (active) this.selectedDeviceButton = button;
    });

    if (rebuild || !this.modelsList.children.length) {
      this.modelsList.innerHTML = this.buildModelTiles(deviceName);
    }

    if (this.step2Title) {
      const template = this.dataset.step2Template || '__DEVICE__';
      this.step2Title.textContent = template.replace('__DEVICE__', deviceName);
    }

    this.hideTable();
    if (this.modelPanel) this.modelPanel.hidden = false;

    if (this.status && this.step2Title) this.status.textContent = this.step2Title.textContent;
    if (scroll) this.scrollTo(this.modelPanel);
    if (focus && this.step2Title) this.step2Title.focus({ preventScroll: true });
  }

  selectModel(modelKey, { scroll = false } = {}) {
    if (!modelKey) return;
    this.currentModel = modelKey;

    this.modelsList?.querySelectorAll('[data-model]').forEach((tile) => {
      const active = tile.dataset.model === modelKey;
      tile.setAttribute('aria-expanded', String(active));
      tile.classList.toggle('is-selected', active);
    });

    this.renderTable(this.currentDevice, modelKey);
    if (scroll && this.results && !this.results.hidden) this.scrollTo(this.results);
  }

  buildModelTiles(deviceName) {
    const device = this.models?.devices?.find((entry) => entry.name === deviceName);
    if (device) {
      return device.groups
        .map((group, groupIndex) => {
          const titleId = `${this.panelId}-g${groupIndex}`;
          const items = group.models
            .map((tile) => {
              const sub = tile.sub ? `<span class="model-tile__sub">${this.esc(tile.sub)}</span>` : '';
              return (
                `<li class="model-grid__item"><button class="model-tile" type="button" data-model="${this.esc(tile.key)}" data-slug="${this.esc(tile.slug)}" aria-expanded="false">` +
                `<svg class="model-tile__art" viewBox="0 0 120 120" aria-hidden="true" focusable="false" style="${this.esc(tile.style)}"><use href="#${this.esc(tile.art)}"></use></svg>` +
                `<span class="model-tile__name">${this.esc(tile.title)}</span>${sub}</button></li>`
              );
            })
            .join('');
          return `<div class="model-group"><h3 class="model-group__title" id="${titleId}">${this.esc(group.title)}</h3><ul class="model-grid" role="list" aria-labelledby="${titleId}">${items}</ul></div>`;
        })
        .join('');
    }
    return this.buildModelTilesFromRows(deviceName);
  }

  // Repli sans illustrations si `repair-models.json` est indisponible.
  buildModelTilesFromRows(deviceName) {
    const models = this.unique(this.rows.filter((row) => row.device === deviceName).map((row) => row.model));
    if (models.length === 0) return '';
    const items = models
      .map((model) => {
        const tag = this.rows.find((row) => row.device === deviceName && row.model === model)?.modelTag || '';
        return (
          `<li class="model-grid__item"><button class="model-tile model-tile--plain" type="button" data-model="${this.esc(model)}" data-slug="${this.esc(tag)}" aria-expanded="false">` +
          `<span class="model-tile__name">${this.esc(model)}</span></button></li>`
        );
      })
      .join('');
    return `<div class="model-group"><ul class="model-grid" role="list">${items}</ul></div>`;
  }

  hideTable() {
    if (this.results) this.results.hidden = true;
    if (this.status) this.status.textContent = '';
    if (this.modelLink) this.modelLink.hidden = true;
    this.toggle(this.empty, false);
  }

  renderTable(deviceName, modelKey) {
    const rows = this.rows
      .filter((row) => row.device === deviceName && row.model === modelKey)
      .sort((a, b) => this.collator.compare(a.issue, b.issue));

    if (!modelKey || rows.length === 0 || !this.rowsBody || !this.results) {
      if (this.results) this.results.hidden = true;
      this.toggle(this.empty, Boolean(modelKey) && rows.length === 0);
      return;
    }

    const fragment = document.createDocumentFragment();
    rows.forEach((row) => {
      const tr = document.createElement('tr');

      const issueCell = document.createElement('th');
      issueCell.scope = 'row';
      const issueLink = document.createElement('a');
      issueLink.href = row.url;
      issueLink.textContent = row.issue;
      issueCell.append(issueLink);

      const priceCell = document.createElement('td');
      priceCell.className = 'price-table__price';
      priceCell.textContent = row.price === null ? '—' : this.money.format(row.price / 100);

      // Le nom de la réparation mène à sa fiche ; le bouton ouvre un devis
      // pré-rempli où il ne reste que l'e-mail et le téléphone à saisir.
      const actionCell = document.createElement('td');
      actionCell.className = 'price-table__action';
      const action = document.createElement('a');
      action.className = 'price-table__cta';
      action.href = this.quoteHref(row);
      action.textContent = this.dataset.requestLabel || this.dataset.viewLabel || '→';
      action.setAttribute('aria-label', `${action.textContent} : ${row.issue}, ${row.model}`);
      action.dataset.track = 'finder_request';
      actionCell.append(action);

      tr.append(issueCell, priceCell, actionCell);
      fragment.append(tr);
    });

    this.rowsBody.replaceChildren(fragment);
    if (this.resultsTitle) {
      const template = this.dataset.headingTemplate || '__MODEL__';
      this.resultsTitle.textContent = template.replace('__MODEL__', modelKey);
    }
    this.updateModelLink(rows);
    this.results.hidden = false;
    this.toggle(this.empty, false);
    if (this.status) {
      const template = this.dataset.statusTemplate || '__COUNT__ __MODEL__';
      this.status.textContent = template.replace('__COUNT__', String(rows.length)).replace('__MODEL__', modelKey);
    }
  }

  quoteHref(row) {
    const base = this.dataset.quoteUrl;
    if (!base) return row.url;
    const url = new URL(base, window.location.origin);
    url.searchParams.set('appareil', row.model);
    url.searchParams.set('panne', row.issue);
    url.hash = 'devis';
    return `${url.pathname}${url.search}${url.hash}`;
  }

  updateModelLink(rows) {
    if (!this.modelLink) return;
    const modelTag = rows.find((row) => row.modelTag)?.modelTag;
    const collectionUrl = this.collectionUrls[this.currentDevice];
    if (!modelTag || !collectionUrl) {
      this.modelLink.hidden = true;
      return;
    }
    const base = collectionUrl.endsWith('/') ? collectionUrl.slice(0, -1) : collectionUrl;
    this.modelLink.href = `${base}/${modelTag}`;
    this.modelLink.hidden = false;
  }

  matchDevice(value) {
    if (!value) return '';
    const target = this.normalize(value);
    const names = this.models?.devices?.map((entry) => entry.name) || this.unique(this.rows.map((row) => row.device));
    return names.find((name) => this.normalize(name) === target) || '';
  }

  matchModel(deviceName, value) {
    if (!value) return '';
    const target = this.normalize(value);
    const device = this.models?.devices?.find((entry) => entry.name === deviceName);
    const candidates = device
      ? device.groups.flatMap((group) => group.models)
      : this.unique(this.rows.filter((row) => row.device === deviceName).map((row) => row.model)).map((model) => ({ key: model, slug: '' }));
    const found = candidates.find((tile) => this.normalize(tile.key) === target || (tile.slug && this.normalize(tile.slug) === target));
    return found ? found.key : '';
  }

  normalize(value) {
    return String(value)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '');
  }

  scrollTo(node) {
    if (!node) return;
    node.scrollIntoView({ behavior: this.reduceMotion ? 'auto' : 'smooth', block: 'start' });
  }

  unique(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) => this.collator.compare(a, b));
  }

  esc(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  toggle(node, visible) {
    if (node) node.hidden = !visible;
  }
}

if (!customElements.get('repair-selector')) {
  customElements.define('repair-selector', RepairSelector);
}
